import dayjs from 'dayjs';
import { z as zod } from 'zod';

// ----------------------------------------------------------------------

type InputProps = {
  message?: {
    required_error?: string;
    invalid_type_error?: string;
    duration_error?: string;
    size_error?: string;
    aspect_ratio_error?: string;
  };
  maxDuration?: number; // in seconds
  maxSize?: number; // in bytes
  aspectRatio?: {
    width: number;
    height: number;
  };
  minFiles?: number;
  isValidPhoneNumber?: (text: string) => boolean;
};

export const schemaHelper = {
  /**
   * Phone number
   * defaultValue === ''
   */
  phoneNumber: (props?: InputProps) =>
    zod
      .string({
        required_error: props?.message?.required_error ?? 'Phone number is required!',
        invalid_type_error: props?.message?.invalid_type_error ?? 'Invalid phone number!',
      })
      .min(1, {
        message: props?.message?.required_error ?? 'Phone number is required!',
      })
      .refine((data) => props?.isValidPhoneNumber?.(data), {
        message: props?.message?.invalid_type_error ?? 'Invalid phone number!',
      }),
  /**
   * Date
   * defaultValue === null
   */
  date: (props?: InputProps) =>
    zod.coerce
      .date()
      .nullable()
      .transform((dateString, ctx) => {
        const date = dayjs(dateString).format();

        const stringToDate = zod.string().pipe(zod.coerce.date());

        if (!dateString) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: props?.message?.required_error ?? 'Date is required!',
          });
          return null;
        }

        if (!stringToDate.safeParse(date).success) {
          ctx.addIssue({
            code: zod.ZodIssueCode.invalid_date,
            message: props?.message?.invalid_type_error ?? 'Invalid Date!!',
          });
        }

        return date;
      })
      .pipe(zod.union([zod.number(), zod.string(), zod.date(), zod.null()])),
  /**
   * Editor
   * defaultValue === '' | <p></p>
   */
  editor: (props?: InputProps) =>
    zod.string().min(8, { message: props?.message?.required_error ?? 'Editor is required!' }),
  /**
   * Object
   * defaultValue === null
   */
  objectOrNull: <T>(props?: InputProps) =>
    zod.custom<T | null>().refine((data) => data !== null && data !== '', {
      message: props?.message?.required_error ?? 'Field is required!',
    }),
  /**
   * Boolean
   * defaultValue === false
   */
  boolean: (props?: InputProps) =>
    zod.coerce.boolean().refine((bool) => bool === true, {
      message: props?.message?.required_error ?? 'Switch is required!',
    }),
  /**
   * File
   * defaultValue === '' || null
   */
  file: (props?: InputProps) =>
    zod.custom<File | string | null>().superRefine(async (data, ctx) => {
      const hasFile = data instanceof File || (typeof data === 'string' && !!data.length);

      if (!hasFile) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: props?.message?.required_error ?? 'File is required!',
        });
        return;
      }

      if (data instanceof File) {
        // Check file size
        if (props?.maxSize && data.size > props.maxSize) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message:
              props.message?.size_error ?? `File must be smaller than ${props.maxSize} bytes`,
          });
        }

        // Check video specific validations
        if (data.type.startsWith('video/')) {
          try {
            const dimensions = await getVideoDimensions(data);

            // Check aspect ratio
            if (props?.aspectRatio) {
              const expectedRatio = props.aspectRatio.width / props.aspectRatio.height;
              const actualRatio = dimensions.width / dimensions.height;

              // Allow for a small margin of error (0.1)
              if (Math.abs(expectedRatio - actualRatio) > 0.1) {
                ctx.addIssue({
                  code: zod.ZodIssueCode.custom,
                  message:
                    props.message?.aspect_ratio_error ??
                    `Video must have a ${props.aspectRatio.width}:${props.aspectRatio.height} aspect ratio`,
                });
              }
            }

            // Check duration if needed
            if (props?.maxDuration && dimensions.duration > props.maxDuration) {
              ctx.addIssue({
                code: zod.ZodIssueCode.custom,
                message:
                  props.message?.duration_error ??
                  `Video must be shorter than ${props.maxDuration} seconds`,
              });
            }
          } catch (error) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: 'Failed to validate video metadata',
            });
          }
        }
      }
    }),
  /**
   * Files
   * defaultValue === []
   */
  files: (props?: InputProps) =>
    zod.array(zod.custom<File | string>()).transform((data, ctx) => {
      const minFiles = props?.minFiles ?? 2;

      if (!data.length) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: props?.message?.required_error ?? 'Files is required!',
        });
      } else if (data.length < minFiles) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: `Must have at least ${minFiles} items!`,
        });
      }

      return data;
    }),
  url: (props?: InputProps) =>
    zod
      .string()
      .optional()
      .refine(
        (val) =>
          !val ||
          /^https:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/i.test(
            val
          ),
        {
          message: props?.message?.invalid_type_error ?? 'Invalid URL (must start with https://)',
        }
      ),
};

const getVideoDimensions = (
  file: File
): Promise<{ width: number; height: number; duration: number }> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    const objectUrl = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video metadata'));
    };

    video.src = objectUrl;
  });
